import type { ConversationFilter } from '@restaurant/api-client';
import { useConversations } from '@restaurant/api-client/react';
import type { ConversationDto } from '@restaurant/shared';
import { Clock, MessageSquare, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { EmptyState, Skeleton, Tabs, TabsList, TabsTrigger } from '@/components/ui/misc';
import { cn, formatPhone, initials } from '@/lib/utils';
import { INTENT_LABEL, listTime, windowState } from './format';

export function ConversationList({ activeId }: { activeId?: string }) {
  const [filter, setFilter] = useState<ConversationFilter>('all');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);
  const q = useConversations(filter, debounced);
  const rows = q.data?.pages.flatMap((p) => p.data) ?? [];

  return (
    <div className="flex h-full flex-col">
      <div className="space-y-2 border-b p-3">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or number" className="pl-8" aria-label="Search conversations" />
        </div>
        <Tabs value={filter} onValueChange={(v) => setFilter(v as ConversationFilter)}>
          <TabsList className="w-full">
            <TabsTrigger value="all" className="flex-1">All</TabsTrigger>
            <TabsTrigger value="unread" className="flex-1">Unread</TabsTrigger>
            <TabsTrigger value="mine" className="flex-1">Mine</TabsTrigger>
            <TabsTrigger value="open" className="flex-1">Open</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      <div
        className="min-h-0 flex-1 overflow-y-auto"
        onScroll={(e) => {
          const el = e.currentTarget;
          if (el.scrollHeight - el.scrollTop - el.clientHeight < 200 && q.hasNextPage && !q.isFetchingNextPage) void q.fetchNextPage();
        }}
      >
        {q.isLoading ? (
          <div className="space-y-3 p-3">
            {Array.from({ length: 6 }, (_, i) => (
              <Skeleton key={i} className="h-14" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <EmptyState icon={<MessageSquare />} title="No conversations" description={debounced ? 'Nothing matches your search.' : 'Customer messages will appear here.'} />
        ) : (
          rows.map((c) => <Row key={c.id} c={c} active={c.id === activeId} />)
        )}
      </div>
    </div>
  );
}

function Row({ c, active }: { c: ConversationDto; active: boolean }) {
  const name = c.customer.name ?? formatPhone(c.customer.waId);
  const win = windowState(c.windowOpenUntil);
  const intent = c.aiIntent ? INTENT_LABEL[c.aiIntent] : undefined;
  return (
    <Link
      to={`/inbox/${c.id}`}
      className={cn('flex gap-3 border-b px-3 py-2.5 transition-colors hover:bg-accent/60', active && 'bg-accent')}
    >
      <div className="relative flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/12 text-sm font-semibold text-primary">
        {initials(name)}
        {win.open ? <span className="absolute -bottom-0.5 -right-0.5 size-3 rounded-full border-2 border-card bg-success" title={win.label} /> : null}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <p className={cn('truncate text-sm', c.unreadCount ? 'font-semibold' : 'font-medium')}>{name}</p>
          <span className={cn('shrink-0 text-xs', c.unreadCount ? 'font-medium text-primary' : 'text-muted-foreground')}>{listTime(c.lastMessageAt)}</span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <p className="truncate text-xs text-muted-foreground">
            {c.lastMessageDirection === 'outbound' ? 'You: ' : ''}
            {c.lastMessagePreview}
          </p>
          <div className="flex shrink-0 items-center gap-1">
            {c.draftOrderId ? <Clock className="size-3.5 text-warning" aria-label="Draft order" /> : null}
            {intent ? <Badge variant={intent.variant} className="px-1.5 py-0 text-[10px]">{intent.label}</Badge> : null}
            {c.unreadCount ? <span className="min-w-5 rounded-full bg-primary px-1.5 text-center text-[11px] font-semibold text-primary-foreground">{c.unreadCount}</span> : null}
          </div>
        </div>
      </div>
    </Link>
  );
}
